import {
    Box,
    DynamicDatagrid,
    GridColDef,
    GridToolbarColumnsButton,
    GridToolbarContainer,
    GridToolbarDensitySelector,
    GridToolbarQuickFilter,
} from "@leap4/frontend";
import React, { useMemo } from "react";
import type { DashboardBreakdownDto, DashboardBreakdownRowDto } from "../../../api/generated/models";
import { DASH, formatMetric, formatSigned } from "./format";

interface IProps {
    breakdown: DashboardBreakdownDto | undefined;
    /** Explains what every percentage is measured against, on hover. */
    baselineTitle: string | null;
    conversionLabel: string;
    rpmLabel: string;
    loading: boolean;
    refresh: () => void;
    /** Opens the segment in Explorer. */
    onRowClick?: (dimValue: string) => void;
}

const Toolbar = (): JSX.Element => (
    <GridToolbarContainer sx={{ display: "flex", alignItems: "center", gap: 1, p: 0.5 }}>
        <GridToolbarQuickFilter sx={{ flex: 1, maxWidth: 280 }} />
        <GridToolbarColumnsButton />
        <GridToolbarDensitySelector />
    </GridToolbarContainer>
);

/** Nulls sort last in BOTH directions — "no data" should never win either end of a sort. */
const nullsLastComparator = (a: unknown, b: unknown): number => {
    const av = typeof a === "number" && Number.isFinite(a) ? a : null;
    const bv = typeof b === "number" && Number.isFinite(b) ? b : null;
    if (av === null && bv === null) return 0;
    if (av === null) return 1;
    if (bv === null) return -1;
    return av - bv;
};

/** Money in the black or in the red, at a glance. Null stays the default colour — it is not zero. */
const signClassName = (params: { value?: unknown }): string => {
    const v = params.value;
    if (typeof v !== "number" || !Number.isFinite(v)) return "";
    return v < 0 ? "dash-negative" : "dash-positive";
};

/** Fixed slot for the change, so every row's value ends and every row's percentage begins on the same */
const PCT_W = 62;

/** Small, colour-coded change beside its parent figure. Blank when there is nothing to compare to. */
const formatPct = (v: number | null): string => {
    if (v === null || !Number.isFinite(v)) return "";
    const pct = v * 100;
    const digits = Math.abs(pct) >= 10 ? 0 : 1;
    return `${pct > 0 ? "▲ +" : pct < 0 ? "▼ " : ""}${pct.toFixed(digits)}%`;
};

/** Whether a rise is good news. */
const changeColor = (v: number | null, higherIsBetter: boolean): string => {
    if (v === null || !Number.isFinite(v) || v === 0) return "text.disabled";
    return (v > 0) === higherIsBetter ? "success.main" : "error.main";
};

const numericCol = (
    field: string,
    headerName: string,
    unit: string,
    opts: {
        compact?: boolean;
        signed?: boolean;
        width?: number;
        hide?: boolean;
        colorBySign?: boolean;
        /** Field carrying this metric's fractional change, rendered beside the value. */
        pctField?: string;
        higherIsBetter?: boolean;
        /** Hover text naming the comparison window. A bare percentage says nothing about "vs what". */
        pctTitle?: string | null;
    } = {}
): GridColDef => {
    const format = (v: number | null): string =>
        opts.signed ? formatSigned(v, unit, opts.compact) : formatMetric(v, unit, opts.compact);

    return {
        field,
        headerName,
        type: "number",
        width: opts.width ?? 120,
        align: "right",
        headerAlign: "right",
        sortComparator: nullsLastComparator,
        ...(opts.colorBySign && !opts.pctField ? { cellClassName: signClassName } : {}),
        ...(opts.pctField
            ? {
                  renderCell: (params: any) => {
                      const v = typeof params.value === "number" ? params.value : null;
                      const pct = (params.row?.[opts.pctField as string] ?? null) as number | null;
                      return (
                          <Box
                              sx={{
                                  display: "flex",
                                  alignItems: "baseline",
                                  gap: 0.75,
                                  width: "100%",
                                  minWidth: 0,
                              }}
                          >
                              <Box
                                  component="span"
                                  sx={{
                                      flex: 1,
                                      textAlign: "right",
                                      minWidth: 0,
                                      color: opts.colorBySign
                                          ? v === null || !Number.isFinite(v)
                                              ? "text.primary"
                                              : v < 0
                                              ? "error.main"
                                              : "success.main"
                                          : "text.primary",
                                  }}
                              >
                                  {format(v)}
                              </Box>
                              <Box
                                  component="span"
                                  title={formatPct(pct) ? opts.pctTitle ?? undefined : undefined}
                                  sx={{
                                      width: PCT_W,
                                      flexShrink: 0,
                                      textAlign: "left",
                                      fontSize: 11,
                                      whiteSpace: "nowrap",
                                      cursor: formatPct(pct) && opts.pctTitle ? "help" : "inherit",
                                      color: changeColor(pct, opts.higherIsBetter !== false),
                                  }}
                              >
                                  {formatPct(pct)}
                              </Box>
                          </Box>
                      );
                  },
              }
            : {
                  // v8 passes the value positionally; destructuring it threw on a null cell.
                  valueFormatter: (value: unknown) =>
                      format(typeof value === "number" ? value : null),
              }),
    };
};

const BreakdownTable = ({
    breakdown,
    baselineTitle,
    conversionLabel,
    rpmLabel,
    loading,
    refresh,
    onRowClick,
}: IProps): JSX.Element => {
    const deltaMode = !!breakdown?.deltaMode;

    const columns = useMemo<GridColDef[]>(() => {
        const cols: GridColDef[] = [
            {
                field: "dimValue",
                headerName: breakdown?.dimensionLabel || "Publisher",
                flex: 1,
                minWidth: 180,
                valueFormatter: (value: unknown) => (value ? String(value) : DASH),
            },
            numericCol("profit", "Profit", "currency", {
                compact: true,
                width: 180,
                colorBySign: true,
                pctField: "profitPct",
                pctTitle: baselineTitle,
            }),
        ];

        // Profit Change is the only contribution column shown by default; the rest via the picker.
        if (deltaMode) {
            cols.push(
                numericCol("profitChange", "Profit Change", "currency", {
                    compact: true,
                    signed: true,
                    width: 140,
                    colorBySign: true,
                })
            );
        }

        cols.push(
            numericCol("edgePer1k", "Edge / 1K", "currency", {
                width: 165,
                colorBySign: true,
                pctField: "edgePct",
                pctTitle: baselineTitle,
            }),
            // Cost per mille rising is bad news, so its change colours the other way round.
            numericCol("cpm", "CPM", "currency", {
                width: 155,
                pctField: "cpmPct",
                pctTitle: baselineTitle,
                higherIsBetter: false,
            }),
            numericCol("rpm", rpmLabel, "currency", { width: 165, pctField: "rpmPct", pctTitle: baselineTitle }),
            numericCol("impressions", "Impressions", "count", {
                compact: true,
                width: 165,
                pctField: "impressionsPct",
                pctTitle: baselineTitle,
            }),
            numericCol("conversions", conversionLabel, "count", {
                width: 180,
                pctField: "conversionsPct",
                pctTitle: baselineTitle,
            }),
            // Lower CPA is better, so its change colours the other way round.
            numericCol("cpa", "CPA", "currency", {
                width: 155,
                pctField: "cpaPct",
                pctTitle: baselineTitle,
                higherIsBetter: false,
            }),
            numericCol("roas", "ROAS", "multiple", {
                width: 155,
                pctField: "roasPct",
                pctTitle: baselineTitle,
            }),
            numericCol("roi", "ROI", "percent", { width: 100 }),
            numericCol("margin", "Margin", "percent", { width: 110 }),
            numericCol("grossRevenue", "Gross Revenue", "currency", { width: 140 }),
            numericCol("takeRate", "Take Rate", "percent", { width: 110 }),
            numericCol("conversionRate", "CR", "percent", { width: 100 }),
            numericCol("visitRate", "VR", "percent", { width: 100 })
        );

        return cols;
    }, [breakdown?.dimensionLabel, deltaMode, conversionLabel, rpmLabel, baselineTitle]);

    // Hidden by default but reachable from the picker, so the contribution maths stays inspectable.
    const hiddenColumns = useMemo<GridColDef[]>(() => {
        const cols = [
            numericCol("cost", "Cost", "currency", { compact: true }),
            numericCol("revenue", "Revenue", "currency", { compact: true }),
            numericCol("visits", "Visits", "count", { compact: true }),
            numericCol("cpmContribution", "CPM Contribution", "currency", { signed: true, width: 160 }),
            numericCol("rpmContribution", `${rpmLabel} Contribution`, "currency", {
                signed: true,
                width: 170,
            }),
            numericCol("edgeContribution", "Edge Contribution", "currency", {
                signed: true,
                width: 160,
            }),
        ];
        if (deltaMode) {
            cols.push(
                numericCol("cpmRateEffect", "CPM Rate Effect", "currency", { signed: true, width: 150 }),
                numericCol("cpmMixEffect", "CPM Mix Effect", "currency", { signed: true, width: 150 }),
                numericCol("rpmRateEffect", `${rpmLabel} Rate Effect`, "currency", {
                    signed: true,
                    width: 160,
                }),
                numericCol("rpmMixEffect", `${rpmLabel} Mix Effect`, "currency", {
                    signed: true,
                    width: 160,
                })
            );
        }
        return cols;
    }, [deltaMode, rpmLabel]);

    const allColumns = useMemo(() => [...columns, ...hiddenColumns], [columns, hiddenColumns]);

    const columnVisibilityModel = useMemo(() => {
        const model: Record<string, boolean> = {};
        for (const c of hiddenColumns) model[c.field] = false;
        return model;
    }, [hiddenColumns]);

    const rows = useMemo(
        () =>
            (breakdown?.rows ?? []).map((r: DashboardBreakdownRowDto, i: number) => ({
                id: `${r.dimValue || "—"}-${i}`,
                ...r,
            })),
        [breakdown?.rows]
    );

    return (
        <DynamicDatagrid
            key={`${breakdown?.dimension ?? "dim"}-${deltaMode ? "delta" : "level"}`}
            rows={rows}
            columns={allColumns as any}
            loading={loading}
            loadError={false}
            refreshFunc={refresh}
            toolbar={Toolbar}
            pageSize={25}
            onRowClick={
                onRowClick
                    ? (params: any) => onRowClick(String(params.row?.dimValue ?? ""))
                    : undefined
            }
            style={{ minHeight: "48vh", width: "100%" }}
            initialState={{ columns: { columnVisibilityModel } }}
            rootSx={{
                "& .dash-negative": { color: "error.main" },
                "& .dash-positive": { color: "success.main" },
                ...(onRowClick ? { "& .MuiDataGrid-row": { cursor: "pointer" } } : {}),
            }}
        />
    );
};

export default BreakdownTable;
